/**
 * LmsSettings — Workspace-level LMS configuration:
 *  - Certificate branding (logo URL, signatory, accent colour)
 *  - Question banks + IQ/EQ preset generator
 * Feb 2026 R11.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Award, Library, Sparkles, Plus, Trash2, Brain } from "lucide-react";
import IqEqBuilder from "./IqEqBuilder";
import CohortScheduler from "./CohortScheduler";

const PRESETS = [
  ["iq_general", "General IQ Assessment"],
  ["iq_pattern", "IQ · Pattern completion"],
  ["iq_verbal", "IQ · Verbal reasoning"],
  ["eq_general", "General EQ Assessment"],
  ["eq_scenario", "EQ · Scenario-based"],
];

export default function LmsSettings() {
  const { activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("branding");
  const [brand, setBrand] = useState(null);
  const [banks, setBanks] = useState([]);
  const [bankOpen, setBankOpen] = useState(false);
  const [newBank, setNewBank] = useState({ title: "", description: "" });
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ preset: "iq_general", prompt: "", audience: "", question_count: 12, time_limit_min: 20 });
  const [genBusy, setGenBusy] = useState(false);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const [b, bs] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/lms/cert-branding`),
        api.get(`/workspaces/${activeWorkspaceId}/lms/question-banks`),
      ]);
      setBrand(b.data);
      setBanks(Array.isArray(bs.data) ? bs.data : []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  const saveBrand = async () => {
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/lms/cert-branding`, brand);
      toast.success("Certificate branding saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const createBank = async () => {
    if (!newBank.title.trim()) return toast.error("Title required");
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/lms/question-banks`, newBank);
      setBankOpen(false);
      setNewBank({ title: "", description: "" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
  };
  const deleteBank = async (bid) => {
    if (!confirm("Delete this bank?")) return;
    try {
      await api.delete(`/workspaces/${activeWorkspaceId}/lms/question-banks/${bid}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const runGenerate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/lms/quizzes/generate-standalone`, gen);
      toast.success(`Generated "${data.title}"`);
      setGenOpen(false);
      navigate(`/app/lms/standalone-quizzes/${data.id}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Generate failed")); }
    finally { setGenBusy(false); }
  };

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="LMS · Settings"
        description="Certificate branding, question banks, IQ/EQ preset generator"
        actions={
          <Button className="rounded-none h-9" onClick={() => setGenOpen(true)} data-testid="lmss-gen-iqeq">
            <Sparkles className="w-4 h-4 mr-1" /> Generate IQ / EQ quiz
          </Button>
        }
      />
      <div className="border-b border-border flex gap-1">
        {[["branding", "Certificate branding"], ["banks", `Question banks (${banks.length})`], ["iqeq", "IQ/EQ Builder"], ["cohort", "Cohort Scheduler"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} className={`px-3 py-1.5 text-xs uppercase tracking-widest ${tab === v ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`} data-testid={`lmss-tab-${v}`}>{l}</button>
        ))}
      </div>

      {tab === "branding" && brand && (
        <div className="max-w-lg space-y-3 border border-border p-4">
          <div className="flex items-center gap-2 mb-2"><Award className="w-4 h-4 text-primary" /><h3 className="text-sm font-semibold">Certificate branding</h3></div>
          <div>
            <Label className="text-xs mb-1 block">Logo URL (rendered at top-center of PDF)</Label>
            <Input value={brand.logo_url || ""} onChange={(e) => setBrand({ ...brand, logo_url: e.target.value })} className="h-9 rounded-none" placeholder="https://…" data-testid="lmss-logo" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Signatory name</Label>
            <Input value={brand.signatory_name || ""} onChange={(e) => setBrand({ ...brand, signatory_name: e.target.value })} className="h-9 rounded-none" placeholder="e.g. Priya Sharma" data-testid="lmss-signatory" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Signatory title</Label>
            <Input value={brand.signatory_title || ""} onChange={(e) => setBrand({ ...brand, signatory_title: e.target.value })} className="h-9 rounded-none" placeholder="Chief People Officer" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Accent color (hex)</Label>
            <Input value={brand.accent_color || "#7c3aed"} onChange={(e) => setBrand({ ...brand, accent_color: e.target.value })} className="h-9 rounded-none w-32" />
          </div>
          <Button onClick={saveBrand} className="rounded-none" data-testid="lmss-save-brand">Save branding</Button>
        </div>
      )}

      {tab === "banks" && (
        <div className="space-y-3">
          <Button onClick={() => setBankOpen(true)} className="rounded-none" data-testid="lmss-new-bank"><Plus className="w-4 h-4 mr-1" /> New bank</Button>
          {banks.length === 0 && <div className="text-sm text-muted-foreground italic">No banks yet.</div>}
          {banks.map((b) => (
            <div key={b.id} className="border border-border p-3 flex items-center gap-3" data-testid={`lmss-bank-${b.id}`}>
              <Library className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <div className="font-semibold text-sm">{b.title}</div>
                {b.description && <div className="text-xs text-muted-foreground">{b.description}</div>}
              </div>
              <span className="chip text-[10px]">{b.question_count} questions</span>
              <button onClick={() => deleteBank(b.id)} className="text-rose-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {tab === "iqeq" && (
        <IqEqBuilder workspaceId={activeWorkspaceId} />
      )}

      {tab === "cohort" && (
        <CohortScheduler workspaceId={activeWorkspaceId} />
      )}

      <Dialog open={bankOpen} onOpenChange={setBankOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader><DialogTitle>New question bank</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input placeholder="Title" value={newBank.title} onChange={(e) => setNewBank({ ...newBank, title: e.target.value })} className="h-9 rounded-none" data-testid="lmss-bank-title" />
            <Input placeholder="Description" value={newBank.description} onChange={(e) => setNewBank({ ...newBank, description: e.target.value })} className="h-9 rounded-none" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setBankOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={createBank} data-testid="lmss-bank-create">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="lmss-gen-dialog">
          <DialogHeader><DialogTitle>Generate IQ / EQ standalone quiz</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label className="text-xs mb-1 block">Preset</Label>
              <Select value={gen.preset} onValueChange={(v) => setGen({ ...gen, preset: v })}>
                <SelectTrigger className="h-9 rounded-none" data-testid="lmss-gen-preset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Extra guidance (optional)</Label>
              <Input value={gen.prompt} onChange={(e) => setGen({ ...gen, prompt: e.target.value })} className="h-9 rounded-none" placeholder="e.g. focus on 3D rotation problems" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Audience</Label>
              <Input value={gen.audience} onChange={(e) => setGen({ ...gen, audience: e.target.value })} className="h-9 rounded-none" placeholder="e.g. Senior candidates" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs mb-1 block">Question count</Label>
                <Input type="number" min={4} max={30} value={gen.question_count} onChange={(e) => setGen({ ...gen, question_count: Number(e.target.value) })} className="h-9 rounded-none" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Time limit (min)</Label>
                <Input type="number" min={1} max={180} value={gen.time_limit_min} onChange={(e) => setGen({ ...gen, time_limit_min: Number(e.target.value) })} className="h-9 rounded-none" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button className="rounded-none" onClick={runGenerate} disabled={genBusy} data-testid="lmss-gen-run">
              {genBusy ? "Generating…" : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
